// Thin, reusable AG Charts wrapper. It injects the shared cube theme (see cubeChartTheme) and a
// transparent background, then renders AgCharts. Callers pass ONLY their data/series/axes via
// `options`; all cross-chart styling lives in the shared theme so one edit updates every chart.
//
// This is the wrapper the rest of the app should reach for instead of importing AgCharts
// directly — existing analytics charts can be migrated onto it over time.
import { AgCharts } from "ag-charts-react";
import type { AgChartOptions } from "ag-charts-community";
import clsx from "clsx";
import { useTheme } from "next-themes";
import { useMemo } from "react";
import { getCubeChartTheme } from "./cubeChartTheme";

interface ICubeAgChart {
  // Chart-specific options (data, series, axes, legend). The wrapper supplies theme + background.
  options: AgChartOptions | any;
  className?: string;
  testId?: string;
}

const CubeAgChart = ({
  options,
  className,
  testId = "CubeAgChartContainer",
}: ICubeAgChart) => {
  const { theme } = useTheme();

  // Merge the caller's options over shared defaults, then force the shared theme last so a
  // chart can't accidentally diverge. background:false lets the widget's own background show.
  const mergedOptions = useMemo<AgChartOptions | any>(
    () => ({
      background: { visible: false },
      ...options,
      theme: getCubeChartTheme(theme),
    }),
    [options, theme],
  );

  return (
    <div
      data-testid={testId}
      id={testId}
      // min-h-0 + overflow-hidden are load-bearing. The widget body is overflow-auto and AG Charts
      // sizes .ag-charts-wrapper in JS from this element, so without clipping one oversized
      // measurement scrolls the widget, which resizes this box, which re-measures the chart.
      className={clsx("flex h-full min-h-0 w-full overflow-hidden", className)}
    >
      <AgCharts className="flex h-full w-full" options={mergedOptions} />
    </div>
  );
};

export default CubeAgChart;
